import { useTerminalsState } from "@/States/Terminals";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import { trpc } from "@/Utils/trpc";
import { useMinimalCustomers } from "@/api/hooks/useCustomers/useMinimalCustomers";
import { defaultTimeFilter } from "@/components/v2/DatePicker/constants";
import { ViewWithLoader } from "@/components/v2/ViewWithLoader";
import { useTrans } from "@/translations";

import { useContext, useMemo, useState } from "react";

import { CardView } from "../../../../components/v2/CardView";
import { HomePageContext } from "../../context";
import { TenantContributionGraph } from "./components/graph";
import { TenantContributionLegend } from "./components/legend";
import { ContributionMode } from "./types";
import { buildContributionData } from "./utils";

export const TenantContribution: React.FC = () => {
  const { t } = useTrans();
  const { timeFilter, selectedTenantCategoryIds } = useContext(HomePageContext);
  const { currentTerminal } = useTerminalsState();
  const { tenantCategories } = useCommonEntitiesStore();
  const { minimalCustomerList } = useMinimalCustomers();

  const [mode, setMode] = useState<ContributionMode>(ContributionMode.TENANT);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const { data: currentPeriod, isLoading: isLoadingCurrentPeriod } =
    trpc.dwh.customers.get.useQuery(
      {
        ...(timeFilter || defaultTimeFilter),
        terminalId: currentTerminal?.id || "",
        tenantCategories: selectedTenantCategoryIds || [],
      },
      {
        queryKeyHashFn: () =>
          [
            "customers",
            currentTerminal?.id,
            timeFilter?.startTimeISO,
            timeFilter?.endTimeISO,
            selectedTenantCategoryIds,
          ].toString(),
        enabled: !!timeFilter && !!currentTerminal,
      },
    );

  const tenantsWithNoCleaners = useMemo(
    () =>
      currentPeriod?.customers.filter(
        (c) =>
          minimalCustomerList.find((mc) => mc.id === c.customerId)
            ?.throwDelegate !== "true",
      ) || [],
    [currentPeriod?.customers, minimalCustomerList],
  );

  const data = useMemo(
    () =>
      buildContributionData({
        mode,
        tenants: tenantsWithNoCleaners,
        tenantCategories,
        t,
      }),
    [mode, tenantsWithNoCleaners, tenantCategories, t],
  );

  const subtitle = useMemo(
    () =>
      mode === "category"
        ? "homePage:tenantContributionSubtitleCategory"
        : "homePage:tenantContributionSubtitleTenant",
    [mode],
  );

  const filters = useMemo(
    () => [
      {
        options: [
          { key: "tenant", label: "homePage:tenantContributionFilterTenant" },
          {
            key: "category",
            label: "homePage:tenantContributionFilterCategory",
          },
        ],
        value: mode,
        onChange: (key: string) => {
          setMode(key as ContributionMode);
          setHoverIndex(null);
        },
      },
    ],
    [mode],
  );

  return (
    <CardView
      title="homePage:tenantContributionTitle"
      subtitle={subtitle}
      filters={filters}
      isLoading={isLoadingCurrentPeriod}
    >
      <ViewWithLoader isLoading={isLoadingCurrentPeriod}>
        <div className="mt-[11.55px] flex flex-col items-center gap-[18px]">
          <TenantContributionGraph
            hoverIndex={hoverIndex}
            setHoverIndex={setHoverIndex}
            data={data}
          />

          <TenantContributionLegend
            hoverIndex={hoverIndex}
            setHoverIndex={setHoverIndex}
            data={data}
          />
        </div>
      </ViewWithLoader>
    </CardView>
  );
};
